import {useEffect, useState} from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import {toast} from "react-toastify";
import {CampaignRepository} from "../../../../../repositories/CampaignRepository";
import {CallStatusChip} from "../CallStatusChip";
import {ConversationLink} from "./ConversationLink";
import {ACTIVE_POLL_INTERVAL, callOutcomeLabels, headerCellSx} from "../../constants";
import {formatDateTime} from "../../utils";
import {usePolling} from "../../hooks/usePolling";

const columns = ["#", "Status", "Outcome", "Called at", "Answered at", "Conversation", "From", "Extension", "Error"];
const cellSx = {fontSize: 13, whiteSpace: "nowrap"};

// Call history of one campaign phone number, newest first
export const CallAttemptsDialog = ({campaignId, phoneNumber, projectId, onClose}) => {
  const open = !!phoneNumber;
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAttempts = () => {
    if (!phoneNumber) return;
    setLoading(true);
    CampaignRepository.getAttempts(campaignId, phoneNumber.id)
      .then(response => setAttempts(response?.data || []))
      .catch(() => toast.error("Failed to load call attempts"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setAttempts([]);
    fetchAttempts();
  }, [phoneNumber?.id]);

  usePolling(fetchAttempts, open && attempts.some(a => a.status === "ACTIVE"), ACTIVE_POLL_INTERVAL);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Call attempts – {phoneNumber?.phoneNumber}</DialogTitle>
      <DialogContent>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map(column => <TableCell key={column} sx={headerCellSx}>{column}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {attempts.map(attempt => (
              <TableRow key={attempt.id}>
                <TableCell sx={cellSx}>{attempt.attemptNumber}</TableCell>
                <TableCell><CallStatusChip status={attempt.status}/></TableCell>
                <TableCell sx={cellSx}>
                  {attempt.callOutcome ? callOutcomeLabels[attempt.callOutcome] || attempt.callOutcome : "-"}
                </TableCell>
                <TableCell sx={cellSx}>{formatDateTime(attempt.dateCreated)}</TableCell>
                <TableCell sx={cellSx}>{formatDateTime(attempt.answeredAt)}</TableCell>
                <TableCell sx={cellSx}>
                  <ConversationLink projectId={projectId} conversationId={attempt.conversationId}/>
                </TableCell>
                <TableCell sx={cellSx}>{attempt.fromNumber || "-"}</TableCell>
                <TableCell sx={cellSx}>{attempt.extension || "-"}</TableCell>
                <TableCell>
                  <Typography fontSize={12} color="error.main">{attempt.errorMessage || ""}</Typography>
                </TableCell>
              </TableRow>
            ))}
            {attempts.length === 0 &&
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{py: 4, color: "text.secondary"}}>
                  {loading ? "Loading..." : "This number hasn't been called yet"}
                </TableCell>
              </TableRow>
            }
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions sx={{px: 3, pb: 2}}>
        <Button variant="ghost" onClick={fetchAttempts}>Refresh</Button>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
